import React from 'react';
import { SyncingLine } from '@dropbox/dig-icons/assets';
import { DebugPanel, PanelComponent } from './debug-panel';
import { useForceUpdate } from 'signer-app/utils/use-force-update';
import { useEventListener } from 'signer-app/utils/use-event-listener';
import {
  WebAppClientNamespaces,
  AppLogSnapshot,
} from 'hello-react/web-app-client/build-web-app-client';
import { Button } from '@dropbox/dig-components/buttons';
import invariant from 'invariant';
import { TextInput } from '@dropbox/dig-components/text_fields';
import {
  globalAppActionsLog,
  loggingEnabled,
} from 'signer-app/utils/log-app-actions';
import { zenPage } from 'signer-app/signer-experience/story-data';
import hsFetch from 'js/sign-components/common/hs-fetch';

const copy = async (item: any) => {
  navigator.clipboard.writeText(item);
};

type PricingPlanOption = {
  guid: string;
  name: string;
  tier: string | null;
};

const buttonLabelStyle: React.CSSProperties = {
  lineHeight: 1.2,
};

const getSeedProgressivePlanUrl = (planId: string) => {
  const params = new URLSearchParams({
    plan_id: planId,
    period: 'Y',
    source: 'debug-tools',
  });
  return `/pricing/selectPlanForSignUp?${params.toString()}`;
};

const getPricingPlansFromPayload = (payload: any): any[] => {
  if (Array.isArray(payload?.subscription_plans)) {
    return payload.subscription_plans;
  }
  if (Array.isArray(payload?.subscriptionPlans)) {
    return payload.subscriptionPlans;
  }
  return [];
};

const getPlanGuid = (plan: any): string => {
  if (typeof plan?.guid === 'string') {
    return plan.guid;
  }
  if (typeof plan?.plan_id === 'string') {
    return plan.plan_id;
  }
  return '';
};

const parsePricingPlans = (payload: any): PricingPlanOption[] => {
  const rawPlans = getPricingPlansFromPayload(payload);

  return rawPlans
    .map((plan: any) => ({
      guid: getPlanGuid(plan),
      name: typeof plan?.name === 'string' ? plan.name : '',
      tier: typeof plan?.tier === 'string' ? plan.tier : null,
    }))
    .filter((plan: PricingPlanOption) => plan.guid.length > 0);
};

const ProgressiveAuthDebugControls = ({ isOpen }: { isOpen: boolean }) => {
  const [availablePlans, setAvailablePlans] = React.useState<
    PricingPlanOption[]
  >([]);
  const [selectedPlanId, setSelectedPlanId] = React.useState('');
  const [isLoadingPlans, setIsLoadingPlans] = React.useState(false);
  const [isOpeningLogin, setIsOpeningLogin] = React.useState(false);
  const [planError, setPlanError] = React.useState('');

  const loadPlans = React.useCallback(async () => {
    setIsLoadingPlans(true);
    setPlanError('');
    try {
      const response = await hsFetch('/pricing/getPricingData');
      if (!response.ok) {
        throw new Error(`Failed to load plans (${response.status})`);
      }
      const payload = await response.json();
      const parsedPlans = parsePricingPlans(payload);
      const progressivePlans = parsedPlans.filter((plan) => {
        const normalized = `${plan.name} ${plan.tier ?? ''}`.toLowerCase();
        return (
          normalized.includes('essentials') || normalized.includes('standard')
        );
      });
      const plansToShow =
        progressivePlans.length > 0 ? progressivePlans : parsedPlans;

      setAvailablePlans(plansToShow);
      if (!selectedPlanId && plansToShow[0]?.guid) {
        setSelectedPlanId(plansToShow[0].guid);
      }
    } catch (err) {
      setPlanError(
        err instanceof Error ? err.message : 'Failed to load pricing plans.',
      );
    } finally {
      setIsLoadingPlans(false);
    }
  }, [selectedPlanId]);

  React.useEffect(() => {
    if (isOpen && availablePlans.length === 0 && !isLoadingPlans) {
      loadPlans();
    }
  }, [availablePlans.length, isLoadingPlans, isOpen, loadPlans]);

  const openProgressiveSignUp = React.useCallback(() => {
    const planId = selectedPlanId.trim();
    if (!planId) {
      return;
    }
    window.location.assign(getSeedProgressivePlanUrl(planId));
  }, [selectedPlanId]);

  const openProgressiveLogIn = React.useCallback(async () => {
    const planId = selectedPlanId.trim();
    if (!planId) {
      return;
    }

    setIsOpeningLogin(true);
    setPlanError('');
    try {
      const response = await hsFetch(getSeedProgressivePlanUrl(planId));
      if (!response.ok) {
        throw new Error(`Failed to seed selected plan (${response.status})`);
      }
      window.location.assign('/account/logIn');
    } catch (err) {
      setPlanError(
        err instanceof Error
          ? err.message
          : 'Failed to seed selected plan for log in.',
      );
    } finally {
      setIsOpeningLogin(false);
    }
  }, [selectedPlanId]);

  return (
    <React.Fragment>
      <DebugPanel.SectionHeader>Progressive Auth</DebugPanel.SectionHeader>
      <div style={{ marginBottom: '8px' }}>
        <Button
          variant="outline"
          size="large"
          onClick={loadPlans}
          isLoading={isLoadingPlans}
          disabled={isLoadingPlans}
        >
          <span style={buttonLabelStyle}>Refresh plans</span>
        </Button>
      </div>
      <div>Select a plan and open progressive sign up or log in.</div>
      <select
        value={selectedPlanId}
        onChange={(event) => setSelectedPlanId(event.target.value)}
        style={{ width: '100%', marginBottom: '8px' }}
      >
        <option value="">Choose a plan</option>
        {availablePlans.map((plan) => {
          const tierSuffix = plan.tier ? ` (${plan.tier})` : '';
          return (
            <option key={plan.guid} value={plan.guid}>
              {plan.name}
              {tierSuffix} [{plan.guid}]
            </option>
          );
        })}
      </select>
      <TextInput
        placeholder="Or paste a plan short guid"
        value={selectedPlanId}
        onChange={(event) => setSelectedPlanId(event.target.value)}
      />
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px',
          marginTop: '8px',
          marginBottom: '12px',
          paddingBottom: '2px',
        }}
      >
        <Button
          variant="primary"
          size="large"
          disabled={!selectedPlanId.trim()}
          onClick={openProgressiveSignUp}
        >
          <span style={buttonLabelStyle}>Open Progressive Sign Up</span>
        </Button>
        <Button
          variant="primary"
          size="large"
          disabled={!selectedPlanId.trim() || isOpeningLogin}
          isLoading={isOpeningLogin}
          onClick={openProgressiveLogIn}
        >
          <span style={buttonLabelStyle}>Open Progressive Log In</span>
        </Button>
      </div>
      {planError && <div>{planError}</div>}
    </React.Fragment>
  );
};

const replaceData = (
  data: AppLogSnapshot<WebAppClientNamespaces>,
): AppLogSnapshot<WebAppClientNamespaces> => {
  // Page URLs are not valid in snapshots, so they're replaced with a
  // placeholder. zenPage is a 1x1 pixel using Dropbox's Zen color.
  data.signer?.loadData?.forEach((d) => {
    (d.result as any)?.pages?.forEach((p: any) => {
      p.src = zenPage;
    });
  });

  return data;
};

const makeStory = (
  name: string,
  data: AppLogSnapshot<WebAppClientNamespaces>,
) => {
  const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);
  const snapshot = replaceData(data);

  return `
// Adjust this to point to the correct story file
import * as stories from './application.stories';
import React from 'react';
import { WebClientNamespaces, AppLogSnapshot } from 'hello-react/web-app-client/build-web-app-client';
import { mockWebAppClient } from 'hello-react/web-app-client/mock-web-app-client';

const ${name}: AppLogSnapshot<WebClientNamespaces> = ${JSON.stringify(
    snapshot,
    null,
    2,
  )};

export const ${capitalizedName}Story = () => {
  const appActions = React.useMemo(() => mockWebAppClient('default', ${name}), []);

  return <stories.Main appActions={appActions} />;
}

export default {
  title: 'TestSnapshot/${name}',
  parameters: {
    layout: 'fullscreen',
    blockGlobalDecorator: true,
  },
}
`.trimStart();
};

const AppActionsPanel: PanelComponent = ({ isOpen }) => {
  /**
   * WARNING: This variable is mutated, which means it doesn't work correctly
   * with hooks.
   */
  const log = globalAppActionsLog;
  /**
   * Because the log is append only, this will change when the array changes, so
   * the useMemo will work as expected. It might look cleaner to clone
   * globalAppActionsLog, but then useMemo is going to recompute on EVERY
   * re-render.
   */
  const logLengthHookHack = log.length;
  const forceUpdate = useForceUpdate();
  useEventListener(document.body, 'app-action-log', () => {
    if (isOpen) {
      forceUpdate();
    }
  });

  const data = React.useMemo(
    () =>
      log.reduce((out, d) => {
        if (!isOpen || logLengthHookHack === 0) {
          return {};
        }
        const path = d.name.split('.');

        const leafNode = path.reduce((out: any, ns: string, i, arr) => {
          if (i === arr.length - 1) {
            out[ns] ??= [];
            out[ns].push({
              args: d.args,
              result: d.result,
            });
          } else {
            out[ns] ??= {};
          }
          return out[ns];
        }, out);
        invariant(Array.isArray(leafNode), 'leafNode should be an array');

        return out;
      }, {} as AppLogSnapshot<WebAppClientNamespaces>),
    [isOpen, log, logLengthHookHack],
  );

  const [name, setName] = React.useState('snapshot');

  if (!isOpen) {
    return null;
  }

  if (!loggingEnabled) {
    return (
      <React.Fragment>
        <ProgressiveAuthDebugControls isOpen={isOpen} />
        <DebugPanel.SectionHeader>WebAppClient</DebugPanel.SectionHeader>
        <div>
          If enabled, this will log everything that passes through WebAppClient
          in a big array.
        </div>
        <Button
          variant="primary"
          onClick={() => {
            window.localStorage.debug = 'hs:web-app-client';
            window.location.reload();
          }}
        >
          Enable and refresh
        </Button>
      </React.Fragment>
    );
  }

  const code = makeStory(name, data);
  return (
    <React.Fragment>
      <ProgressiveAuthDebugControls isOpen={isOpen} />
      <DebugPanel.SectionHeader>
        Actions
        <Button variant="outline" onClick={() => copy(code)}>
          {' '}
          Copy{' '}
        </Button>
      </DebugPanel.SectionHeader>
      <TextInput
        value={name}
        onChange={(e) => setName(e.target.value.replace(/\s+/g, ''))}
      />

      <pre style={{ whiteSpace: 'pre', overflow: 'auto' }}>{code}</pre>
      <Button
        variant="primary"
        onClick={() => {
          window.localStorage.debug = '';
          window.location.reload();
        }}
      >
        Disable and refresh
      </Button>
    </React.Fragment>
  );
};

export const appActionsPanel = NODE_ENV !== 'production' && (
  <DebugPanel
    Component={AppActionsPanel}
    updateWhileClosed={true}
    icon={SyncingLine}
    title="App Actions"
  />
);
